import { create } from 'zustand';

export type AuthUser = {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
  // Set server-side after an admin generates a temp password. While true the
  // POS forces the change-password screen; cleared once the staffer rotates it.
  mustChangePassword: boolean;
  // Resolved on login from the JWT. A scoped cashier sees branchId set; a
  // super_admin / admin sees null and may pick any branch.
  branchId: string | null;
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  setMustChangePassword: (value: boolean) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  setSession: (token, user) => set({ token, user }),
  setMustChangePassword: (value) =>
    set((s) => (s.user ? { user: { ...s.user, mustChangePassword: value } } : s)),
  clear: () => set({ token: null, user: null }),
}));

export function hasPerm(perm: string): boolean {
  const { user } = useAuthStore.getState();
  if (!user) return false;
  if (user.roles.includes('super_admin')) return true;
  return user.perms.includes(perm);
}

/**
 * Reactive `hasPerm` for components — re-renders when the auth store changes
 * (mirrors the admin SPA's hook). The non-React `hasPerm` above reads a
 * snapshot and is fine inside event handlers, but a nav rendered from it would
 * not update when the session is restored or swapped.
 *
 * NOTE: perms are baked into the JWT at login, so granting a perm in the admin
 * Access page only reaches the cashier on their next sign-in / token refresh.
 */
export function useHasPerm(perm: string): boolean {
  return useAuthStore((s) => {
    if (!s.user) return false;
    if (s.user.roles.includes('super_admin')) return true;
    return s.user.perms.includes(perm);
  });
}
